import { withCatalogs } from '@/i18n/catalogs';
import { cachedModule } from '@/platform/lib/cachedModule';
import { loadEmailInterfaceModule, loadEmailListModule } from '@/app/email/emailModules';

export type CoreHomeApplication = 'lists' | 'email' | 'calendar' | 'docs';

/** Marks `rudi:home-navigation:<app>:module-loaded` once the app's code is in. */
export function markHomeNavigationModuleLoaded(appType: CoreHomeApplication): void {
  if (typeof globalThis.performance?.mark === 'function') {
    globalThis.performance.mark(`rudi:home-navigation:${appType}:module-loaded`);
  }
}

export async function trackHomeNavigationModule<T>(
  appType: CoreHomeApplication,
  loader: () => Promise<T>
): Promise<T> {
  const module = await loader();
  markHomeNavigationModuleLoaded(appType);
  return module;
}

/** The import a pointer or keyboard intent on a Home card starts. */
export function trackIntentPreloadedHomeModule<T>(
  appType: CoreHomeApplication,
  loader: () => Promise<T>
): Promise<T> {
  return trackHomeNavigationModule(appType, loader);
}

export const loadListsModule = cachedModule(() =>
  trackHomeNavigationModule(
    'lists',
    withCatalogs(
      [
        'calendar',
        'clinicalTrials',
        'docs',
        'email',
        'forms',
        'labels',
        'lists',
        'platform',
        'publicBooking',
        'settings',
        'training',
      ],
      () => import('@/app/lists/components/ListsOverview')
    )
  )
);

export const loadListViewModule = cachedModule(() =>
  trackHomeNavigationModule(
    'lists',
    withCatalogs(
      [
        'calendar',
        'canvas',
        'chat',
        'clinicalTrials',
        'dashboards',
        'docs',
        'email',
        'forms',
        'investigator',
        'lists',
        'meetingCapture',
        'meetingOutputs',
        'mobile',
        'patients',
        'platform',
        'publicBooking',
        'settings',
        'teamChat',
        'templates',
        'training',
        'vaultItems',
        'video',
        'voice',
      ],
      () => import('@/platform/lists/components/ListView')
    )
  )
);

// The view, its mailbox and the message list load in one round, so the inbox
// renders without a second chunk request after the first.
export const loadEmailModule = cachedModule(() =>
  trackHomeNavigationModule('email', async () => {
    const [view] = await Promise.all([
      withCatalogs(
        ['email', 'organizationMailbox', 'platform', 'settings'],
        () => import('@/app/email/components')
      )(),
      loadEmailInterfaceModule(),
      loadEmailListModule(),
    ]);
    return view;
  })
);

export const loadCalendarModule = cachedModule(() =>
  trackHomeNavigationModule(
    'calendar',
    withCatalogs(
      [
        'calendar',
        'chat',
        'clinicalTrials',
        'docs',
        'email',
        'labels',
        'lists',
        'meetingCapture',
        'meetingOutputs',
        'platform',
        'publicBooking',
        'settings',
        'teamChat',
        'video',
        'voice',
      ],
      () => import('@/app/calendar/components')
    )
  )
);

export const loadDocsModule = cachedModule(() =>
  trackHomeNavigationModule(
    'docs',
    withCatalogs(
      [
        'calendar',
        'canvas',
        'chat',
        'clinicalTrials',
        'dictations',
        'docs',
        'email',
        'labels',
        'lists',
        'meetingCapture',
        'meetingOutputs',
        'patients',
        'platform',
        'publicBooking',
        'recordings',
        'settings',
        'teamChat',
        'training',
        'vaultItems',
        'video',
        'voice',
      ],
      () => import('@/app/docs/components')
    )
  )
);

const moduleLoaders: Record<CoreHomeApplication, () => Promise<unknown>> = {
  lists: loadListsModule,
  email: loadEmailModule,
  calendar: loadCalendarModule,
  docs: loadDocsModule,
};

export function preloadCoreHomeApplication(appType: CoreHomeApplication): void {
  void moduleLoaders[appType]();
}
